import React, { useEffect, useState } from 'react'
import {
  grantConsent,
  hasConsent,
  hasGPC,
  isDismissedThisSession,
  dismissForSession,
} from './consent'

/* Consent banner (accept-only) — CA privacy hardening, Oct 2026.

   Same behavior as the WebPerfex / Bay Area Siding Co / GVD banner:
   - Renders null until after mount, so SSR HTML and the first client
     render match (no hydration mismatch).
   - No banner for GPC browsers, visitors who already accepted, or
     visitors who closed it this session.
   - Accept = 12-month cookie + GTM loads (see consent.js). The X only
     hides the banner for this session; nothing loads.
   - No visible "Decline" wording by design (no CA statute requires a
     reject button; a small X keeps accept rates high).
   - Bottom-right card on desktop, bottom sheet on mobile, z-index
     above the sticky mobile CTA bar. */
const ConsentBanner = () => {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (hasGPC() || hasConsent() || isDismissedThisSession()) return
    setVisible(true)
  }, [])

  // Lets CSS move any other fixed bottom UI out of the way.
  useEffect(() => {
    if (typeof document === 'undefined') return
    document.body.classList.toggle('consent-bar-open', visible)
    return () => document.body.classList.remove('consent-bar-open')
  }, [visible])

  if (!visible) return null

  const onAccept = () => {
    grantConsent()
    setVisible(false)
  }

  const onDismiss = () => {
    dismissForSession()
    setVisible(false)
  }

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed z-[10000] inset-x-2.5 bottom-[calc(10px+env(safe-area-inset-bottom,0px))] md:inset-x-auto md:right-5 md:bottom-5 md:w-[340px] bg-white rounded-xl border border-gray-100 shadow-xl p-4 md:p-5 box-border"
    >
      <button
        type="button"
        aria-label="Close and continue without accepting"
        onClick={onDismiss}
        className="absolute top-1.5 right-1.5 p-2 leading-none text-gray-500 hover:text-gray-700 bg-transparent border-0 cursor-pointer text-[15px] font-bold"
      >
        ✕
      </button>
      <p className="m-0 mb-3 pr-6 text-[13px] md:text-sm leading-relaxed text-gray-600">
        <strong className="text-gray-900">We value your privacy.</strong> We
        use cookies to analyze site traffic and measure our advertising.{' '}
        <a
          href="/privacy-policy/"
          rel="nofollow"
          className="text-brand-700 font-semibold underline whitespace-nowrap"
        >
          Privacy Policy
        </a>
      </p>
      <button
        type="button"
        onClick={onAccept}
        className="block w-full py-2.5 px-5 text-sm md:text-base rounded-full font-bold text-white bg-brand-600 hover:bg-brand-700 focus:ring-4 focus:ring-brand-200 outline-none border-0 cursor-pointer transition duration-200 ease-in-out"
      >
        Accept
      </button>
    </div>
  )
}

export default ConsentBanner
